"use client"
import { useCallback, useMemo } from "react"
import {
  useSwitchChain,
  useWaitForTransactionReceipt,
  useWriteContract,
} from "wagmi"
import { getChainId } from "@/util/crypto"
import { SimulateContractReturnType } from "viem"
import useOnRightChain from "@/hooks/crypto/useOnRightChain"
import { CHAINS } from "@/types/collectibles"

/**
 * Performs actual collectible minting, given a chain and a
 * simulated request.
 *
 * Returns:
 * - txReceipt: Transaction receipt of the minting transaction, ONLY when mined
 * - txHash: Hash of the minting transaction, regardless of whether it's mined
 * - onClick: Function to trigger the minting transaction
 */
export function useMintCollectible({
  chain,
  simulateRequest,
}: {
  chain: CHAINS
  simulateRequest: SimulateContractReturnType
}) {
  const { onRightChain, desiredChainId } = useOnRightChain({
    chain: chain,
  })

  const { switchChainAsync } = useSwitchChain()
  const writeData = useWriteContract()

  const chainId = useMemo(() => getChainId(chain), [chain])

  const txReceipt = useWaitForTransactionReceipt({
    hash: writeData.data,
    chainId,
    confirmations: 2,
    pollingInterval: 1000,
  })

  const handleWrite = useCallback(() => {
    console.log("Writing data", simulateRequest.request, writeData)
    writeData.writeContract(simulateRequest.request)
  }, [simulateRequest?.request, writeData])

  const onClick = useCallback(async () => {
    if (!onRightChain) {
      try {
        await switchChainAsync({ chainId: desiredChainId })
      } catch (error) {
        console.error("Failed to switch chain:", error)
      }
    }

    handleWrite()
  }, [onRightChain, switchChainAsync, desiredChainId, handleWrite])

  return { onClick, txReceipt, txHash: writeData.data }
}
